import { CheckCheck, Copy, ExternalLink, FileText, List, MapPin, Phone, Play, Reply } from "lucide-react";
import { bodySegments, fileKind } from "./templateFormat";

const BUTTON_ICON = { QUICK_REPLY: Reply, URL: ExternalLink, PHONE_NUMBER: Phone, COPY_CODE: Copy };

// WhatsApp's own wallpaper is a faint doodle pattern; two soft dot layers stand in for it.
const WALLPAPER = {
  backgroundColor: "#EFE7DE",
  backgroundImage:
    "radial-gradient(rgba(120,100,80,0.07) 1px, transparent 1.2px), radial-gradient(rgba(120,100,80,0.05) 1px, transparent 1.2px)",
  backgroundSize: "22px 22px, 34px 34px",
  backgroundPosition: "0 0, 11px 11px",
};

const BUTTON_ROW =
  "flex items-center justify-center gap-2 rounded-xl bg-white px-3 py-[9px] text-[14px] font-medium leading-5 text-[#027EB5] shadow-[0_1px_1px_rgba(11,20,26,0.13)]";

function Variable({ n, value }) {
  return (
    <span
      className={`rounded px-1 py-[1px] font-medium ${value ? "bg-[#DCE9F8] text-[#1C467A]" : "bg-[#FEF3C7] text-[#92400E]"}`}
      title={value ? `Example for {{${n}}}` : `{{${n}}} has no example yet`}
    >
      {value || `{{${n}}}`}
    </span>
  );
}

const fileName = (url) => decodeURIComponent(String(url || "").split("?")[0].split("/").pop() || "document.pdf");

// The file above a message: the picture, a video frame, or the document's name.
function Media({ type, url, rounded = "rounded-lg" }) {
  if (type === "location") {
    return (
      <div className="mb-2 flex h-[110px] items-center justify-center rounded-lg bg-[#D9E6DC] text-[#4B6B55]" role="img" aria-label="Location">
        <MapPin size={30} aria-hidden="true" />
      </div>
    );
  }
  const kind = type === "carousel" ? fileKind(url) || "image" : type;
  if (kind === "document") {
    return (
      <div className={`mb-2 flex items-center gap-3 bg-[#F0F2F5] px-3 py-3 ${rounded}`}>
        <FileText size={26} className="shrink-0 text-[#D93025]" aria-hidden="true" />
        <span className="min-w-0 truncate text-[13.5px] text-[#3B4A54]">{url ? fileName(url) : "Your document"}</span>
      </div>
    );
  }
  if (kind === "video") {
    return (
      <div className={`mb-2 flex h-[130px] items-center justify-center bg-[#1F2C34] text-white ${rounded}`} role="img" aria-label="Video">
        <Play size={32} aria-hidden="true" />
      </div>
    );
  }
  return url ? (
    <img src={url} alt="" className={`mb-2 max-h-[190px] w-full object-cover ${rounded}`} />
  ) : (
    <div className={`mb-2 flex h-[110px] items-center justify-center bg-[#E1E6EA] text-[13px] text-[#667781] ${rounded}`}>Your picture</div>
  );
}

function Cards({ cards }) {
  return (
    <div className="mt-[6px] flex gap-2 overflow-x-auto pb-1" aria-label="Cards">
      {cards.map((card, i) => (
        <div key={i} className="w-[190px] shrink-0 rounded-xl bg-white p-2 shadow-[0_1px_1px_rgba(11,20,26,0.13)]">
          <Media type="carousel" url={card.media_url} />
          {card.body && <p className="m-0 mb-2 break-words text-[13px] leading-[18px] text-[#111B21]">{card.body}</p>}
          <ul className="m-0 flex list-none flex-col gap-[3px] p-0">
            {(card.buttons || []).map((b, j) => {
              const Icon = BUTTON_ICON[b.type] || Reply;
              return (
                <li key={j} className="flex items-center justify-center gap-2 rounded-lg bg-[#F0F2F5] px-2 py-[7px] text-[13px] font-medium text-[#027EB5]">
                  <Icon size={13} aria-hidden="true" />
                  <span className="truncate">{b.text || "Button"}</span>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

// An approximation of how the customer sees the template. Variables show their example value.
export default function WhatsAppPreview({
  brandName,
  header,
  body,
  examples,
  footer,
  buttons = [],
  type = "text",
  mediaUrl = "",
  cards = [],
  otp = null,
  compact = false,
  bare = false,
}) {
  const code = type === "authentication";
  const hasMedia = type === "image" || type === "video" || type === "document" || type === "location";
  const text = code ? "123456 is your verification code." : body;
  const segments = bodySegments(text, examples);
  const codeNote = code && otp?.security_recommendation !== false ? "For your security, do not share this code." : "";
  const expiry = code && otp?.code_expiration_minutes ? `This code expires in ${otp.code_expiration_minutes} minutes.` : "";
  const footerText = code ? expiry : footer;
  const shownButtons = code ? [{ type: "COPY_CODE", text: otp?.button_text || "Copy code" }] : buttons;
  const empty = !text && !header && !footerText && !shownButtons.length && !hasMedia && !cards.length;
  // WhatsApp shows three buttons; with more it shows two and a list behind "See all options".
  const shown = shownButtons.length > 3 ? shownButtons.slice(0, 2) : shownButtons;
  const time = "10:42";

  return (
    <figure
      className={`m-0 overflow-hidden bg-[#EFE7DE] ${bare ? "" : "rounded-2xl border border-[#D9D4CC] shadow-[0_1px_2px_rgba(16,24,40,0.08),0_8px_24px_rgba(16,24,40,0.08)]"}`}
      aria-label="Preview of the WhatsApp message"
    >
      {!compact && (
        <div className="flex items-center gap-3 bg-[#075E54] px-4 py-3 text-white">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-[14px] font-semibold">
            {(brandName || "B").trim().charAt(0).toUpperCase()}
          </span>
          <div className="min-w-0">
            <div className="truncate text-[15px] font-medium leading-5">{brandName || "Your brand"}</div>
            <div className="text-[12px] leading-4 text-white/75">Business account</div>
          </div>
        </div>
      )}

      <div className={compact ? "p-3" : "min-h-[260px] p-4"} style={WALLPAPER}>
        {empty ? (
          <div className="max-w-[82%] rounded-xl rounded-tl-sm border border-dashed border-[#B8AFA3] px-3 py-3 text-[13px] leading-5 text-[#6B6256]">
            Your message appears here as you type it.
          </div>
        ) : (
          <div className={type === "carousel" ? "max-w-full" : "max-w-[88%]"}>
            <div className="max-w-[88%] rounded-xl rounded-tl-sm bg-white px-3 pb-[6px] pt-[8px] text-[14px] leading-[20px] text-[#111B21] shadow-[0_1px_1px_rgba(11,20,26,0.13)]">
              {hasMedia && <Media type={type} url={mediaUrl} />}
              {header && <div className="mb-1 break-words font-semibold">{header}</div>}
              {text ? (
                <p className="m-0 whitespace-pre-wrap break-words">
                  {segments.map((s, i) => (s.type === "var" ? <Variable key={i} n={s.n} value={s.value} /> : <span key={i}>{s.value}</span>))}
                </p>
              ) : (
                <p className="m-0 text-[#8696A0]">The message is empty.</p>
              )}
              {codeNote && <p className="m-0 mt-1 break-words">{codeNote}</p>}
              {footerText && <div className="mt-1 break-words text-[12.5px] leading-[17px] text-[#667781]">{footerText}</div>}
              <div className="mt-[2px] flex items-center justify-end gap-1 text-[11px] leading-4 text-[#667781]">
                {time}
                <CheckCheck size={15} className="text-[#53BDEB]" aria-hidden="true" />
              </div>
            </div>

            {type === "carousel" && cards.length > 0 && <Cards cards={cards} />}

            {shownButtons.length > 0 && (
              <ul className="m-0 mt-[3px] flex max-w-[88%] list-none flex-col gap-[3px] p-0">
                {shown.map((b, i) => {
                  const Icon = BUTTON_ICON[b.type] || Reply;
                  return (
                    <li key={i} className={BUTTON_ROW}>
                      <Icon size={15} aria-hidden="true" />
                      <span className="truncate">{b.type === "COPY_CODE" && !code ? `Copy code${b.example ? ` ${b.example}` : ""}` : b.text || "Button text"}</span>
                    </li>
                  );
                })}
                {shownButtons.length > 3 && (
                  <li className={BUTTON_ROW}>
                    <List size={15} aria-hidden="true" />
                    See all options
                  </li>
                )}
              </ul>
            )}
          </div>
        )}
      </div>
    </figure>
  );
}
